// TODO: Move to EndUserExperiences library.

/**
 * Create a `Guard<T>` instance from a simple type guard function
 * (`<T>(x: any) => x is T`).
 *
 * ```ts
 * const isError: Guard<Error> = guard((x: any): x is Error => x instanceof Error);
 * ```
 *
 * `Guard<T>` has the same callable prototype as the simple guard function,
 * and an additional `intersect()` method.
 *
 * ```ts
 * const isStatusError: Guard<Error & { status: number }> = isError.intersect(
 *   struct().required({ status: typeOf('number') })
 * );
 * ```
 */
export function guard<T>(sourceFn: GuardFn<T>): Guard<T> {
  return 'intersect' in sourceFn
    ? ((sourceFn as unknown) as Guard<T>)
    : Object.assign((x: any): x is T => sourceFn(x), {
        intersect<TGuardFn extends GuardFn<any>>(fn: TGuardFn): Guard<T & Static<TGuardFn>> {
          return guard((x: any): x is T & Static<TGuardFn> => sourceFn(x) && fn(x));
        }
      });
}

/**
 * Cast a guard's type (`Guard<T>`) to readonly (`Guard<Readonly<T>>`). The
 * guard is returned unmodified. This is just a casting shortcut.
 */
export function readonly<TGuard extends Guard<any>>(guard: TGuard): Guard<Readonly<Static<TGuard>>> {
  return guard;
}

/**
 * Match anything (`Guard<any>`).
 */
export const any = guard((_x: any): _x is any => {
  return true;
});

/**
 * Match anything (`Guard<unknown>`).
 */
export const unknown = guard((_x: any): _x is unknown => {
  return true;
});

/**
 * Match `null` or `undefined`.
 */
export const nil = guard((x: any): x is null | undefined => {
  return x == null;
});

/**
 * Create a guard which matches any of two or more type guards.
 *
 * ```ts
 * const a: Guard<A>;
 * const b: Guard<B>;
 * const abc: Guard<A | B> = union(a, b);
 * ```
 */
export function union<TGuardFns extends TwoOrMore<GuardFn<any>>>(...fns: TGuardFns): Guard<Static<TGuardFns>> {
  return guard((x: any): x is Static<TGuardFns> => fns.some(fn => fn(x)));
}

/**
 * Create a guard which matches any of one or more given types, defined by
 * `typeof` strings and/or class constructors.
 *
 * __Example:__
 * ```ts
 * const isNumber: Guard<number> = typeOf('number');
 * const isStringOrRegExp: Guard<string | RegExp> = typeOf(['string', RegExp]);
 * ```
 *
 * An optional predicate second argument can be give to narrow the match to
 * only a subset of the given types.
 *
 * ```ts
 * const isFooPrefixed: Guard<string> = typeOf('string', string => /^foo/.test(string));
 * ```
 */
export function typeOf<TTypeArgs extends OneOrMany<TypeArg>>(
  types: TTypeArgs,
  match?: (x: TypeOf<TTypeArgs>) => boolean
): Guard<TypeOf<TTypeArgs>> {
  const many = asArray(types);
  return guard(
    (x: any): x is TypeOf<TTypeArgs> =>
      many.some((type: TypeArg) => (typeof type === 'string' ? typeof x === type : x instanceof type)) &&
      (match == null || match(x))
  );
}
export const string = typeOf('string');
export const number = typeOf('number');
export const bigint = typeOf('bigint');
export const boolean = typeOf('boolean');
export const func = typeOf('function');
export const object = typeOf('object');
export const symbol = typeOf('symbol');
export const undef = typeOf('undefined');

/**
 * Create a guard which matches a specific value. The guard type will be the
 * value's type.
 *
 * ```ts
 * const isString: Guard<string> = eq('foo');
 * const isFoo: Guard<'foo'> = eq('foo' as const);
 * ```
 */
export function eq<TValue>(value: TValue): Guard<TValue> {
  return guard((x: any): x is TValue => x === value);
}
export const nul = eq(null);

/**
 * Create a guard which matches any of an array of specific values. The guard
 * type will be the union of the value types.
 *
 * ```ts
 * const isNumberOrString: Guard<number | string> = eqAny([1, 'one']);
 * const isOne: Guard<1 | 'one'> = eqAny([1, 'one'] as const);
 * ```
 */
export function eqAny<TValues extends readonly [any, ...any[]]>(values: TValues): Guard<ItemType<TValues>> {
  return guard((x: any): x is ItemType<TValues> => values.includes(x));
}

/**
 * Create a guard which matches arrays of unknown values.
 */
export function array(): Guard<unknown[]>;
/**
 * Create a guard which matches an array when all of its values match one of
 * the given type guards.
 *
 * ```ts
 * const isStringsAndNumbers: Guard<(string | number)[]> = array(typeOf('string' | 'number'))
 * ```
 */
export function array<TGuardFns extends Many<GuardFn<any>>>(...fns: TGuardFns): Guard<Static<TGuardFns>[]>;
export function array(...fns: Predicate[]): Predicate {
  return x => x instanceof Array && (fns.length === 0 || fns.some(fn => x.every(value => fn(value))));
}

/**
 * Match array of unknown values.
 */
export const isArray = array();

/**
 * Create a guard which matches any object (including arrays, but not `null`
 * values). The guard type has an index signature of string or number keys,
 * and unknown values.
 */
export function dictionary(): Guard<Record<string, unknown>>;
/**
 * Create a guard which matches an object (including arrays, but not `null`
 * values) when all of its values match one of the given type guards. The
 * guard type has an index signature of string or number keys, and a value
 * type union the given type guard types.
 *
 * ```ts
 * const isStringDict: Guard<Record<string, string | string[]>> = dictionary(typeOf('string'), array(typeOf('string')))
 * ```
 */
export function dictionary<TGuardFns extends Many<GuardFn<any>>>(
  ...fns: TGuardFns
): Guard<Record<string, Static<TGuardFns>>>;
export function dictionary<TGuardFns extends GuardFn<any>[]>(
  ...fns: TGuardFns
): Guard<Record<string, Static<TGuardFns>>> {
  return guard(
    (x: Record<string, unknown>): x is Record<string, Static<TGuardFns>> =>
      x != null &&
      typeof x === 'object' &&
      (fns.length === 0 || fns.some(fit => Object.keys(x).every(key => fit(x[key]))))
  );
}

/**
 * Match any object (including arrays, but not `null` values). The guard type
 * has an index signature of string or number keys, and unknown values.
 */
export const isDictionary = dictionary();

/**
 * Match a tuple which has length equal to the argument count, and each entry
 * matches the guard at the corresponding argument offset.
 *
 * ```ts
 * const stringNumber: Guard<[string, number]> = tuple(typeOf('string'), typeOf('number'));
 * ```
 */
export function tuple<TGuardTuple extends GuardFn<any>[]>(...fns: TGuardTuple): Guard<TupleType<TGuardTuple>> {
  const fnArr: readonly Predicate[][] = fns.map(fn => asArray(fn));
  return guard(
    (x: any): x is TupleType<TGuardTuple> =>
      x instanceof Array && fnArr.every((keyFns, key) => keyFns.some(fn => fn(x[key])))
  );
}

/**
 * Create a guard which matches any object (including arrays, but not `null`
 * values). The guard type does _not_ have an index signature.
 *
 * ```ts
 * const obj: Guard<{}> = struct();
 * ```
 *
 * The returned guard has `required()` and `optional()` methods for defining
 * properties.
 *
 * ```ts
 * const value: Guard<{ a: string, b?: number }> = struct()
 *   .required({ a: typeOf('string') })
 *   .optional({ b: typeOf('number') });
 * ```
 */
export function struct(): StructGuard<{}> {
  return extendStruct((x: any): x is {} => x != null && typeof x === 'object');
}

function extendStruct<TGuardFn extends GuardFn<any>>(parentFn: TGuardFn): StructGuard<Static<TGuardFn>> {
  const parent = guard(parentFn);
  return Object.assign(parent, {
    required<TGuardFns extends StructGuardFns>(fns: TGuardFns) {
      const fnArr: [string, readonly Predicate[]][] = Object.keys(fns).map(key => [key, asArray(fns[key])]);
      return extendStruct(
        parent.intersect((x: any): x is StructRequired<TGuardFns> =>
          fnArr.every(([key, keyFns]) => keyFns.some(fn => fn(x[key])))
        )
      );
    },
    optional<TGuardFns extends StructGuardFns>(fns: TGuardFns) {
      const fnArr: [string, readonly Predicate[]][] = Object.keys(fns).map(key => [key, asArray(fns[key])]);
      return extendStruct(
        parent.intersect((x: any): x is StructOptional<TGuardFns> =>
          fnArr.every(([key, keyFns]) => x[key] === undefined || keyFns.some(fn => fn(x[key])))
        )
      );
    }
  });
}

function asArray<T>(value: T | Many<T>): T[] {
  return (value instanceof Array ? [...value] : [value]) as T[];
}

/**
 * Derive the type of a type guard.
 *
 * ```ts
 * const isMyType: Guard<{ a: string }> = struct().required({ a: typeOf('string') });
 * type MyType = Static<typeof isMyType>; // { a: string }
 * ```
 */
export type Static<T> = ItemType<T> extends (x: any) => x is infer R ? R : T;
/**
 * Simple type guard function (`(x: any) => x is T`)/
 */
export type GuardFn<T> = (x: any) => x is T;
/**
 * Type guard (`<T>(x: any) => x is T`) with an added `intersect()` method.
 */
export type Guard<T> = GuardFn<T> & {
  /**
   * Intersect the receiver (`Guard<X>`) with the target (`GuardFn<Y>`) to
   * create a _new_ type guard (`Guard<X & Y>`).
   *
   * __Example:__
   * ```ts
   * const x: Guard<X>;
   * const y: Guard<Y>;
   * const xy: Guard<X & Y> = x.intersect(y);
   * ```
   */
  intersect<TGuardFn extends GuardFn<any>>(fn: TGuardFn): Guard<T & Static<TGuardFn>>;
};

type Predicate = (x: any) => boolean;
type Many<T> = [T, ...T[]];
type OneOrMany<T> = T | Many<T>;
type TwoOrMore<T> = [T, T, ...T[]];
type ItemType<T> = T extends readonly any[] ? T[number] : T;

type TypeArg =
  | (new (...args: any[]) => any)
  | 'undefined'
  | 'object'
  | 'boolean'
  | 'number'
  | 'bigint'
  | 'string'
  | 'symbol'
  | 'function';
type TypeDecode<TArg> = TArg extends new (...args: any[]) => any
  ? InstanceType<TArg>
  : TArg extends 'undefined'
  ? undefined
  : TArg extends 'object'
  ? null | Record<string, any>
  : TArg extends 'boolean'
  ? boolean
  : TArg extends 'number'
  ? number
  : TArg extends 'bigint'
  ? bigint
  : TArg extends 'string'
  ? string
  : TArg extends 'symbol'
  ? symbol
  : TArg extends 'function'
  ? (...args: any[]) => any
  : unknown;
type TypeOf<TArg> = TypeDecode<ItemType<TArg>>;

type TupleType<TGuardFns> = {
  [P in keyof TGuardFns]: Static<TGuardFns[P]>;
};

type StructRequired<TGuardFns> = TGuardFns extends Record<string, OneOrMany<GuardFn<any>>>
  ? {
      [P in keyof TGuardFns]: Static<TGuardFns[P]>;
    }
  : never;
type StructOptional<TGuardFns> = TGuardFns extends Record<string, OneOrMany<GuardFn<any>>>
  ? {
      [P in keyof TGuardFns]?: Static<TGuardFns[P]>;
    }
  : never;
type StructGuardFns = Record<string, OneOrMany<GuardFn<any>>>;
type StructGuard<T> = Guard<T> & {
  /**
   * Add required properties to a `struct()` definition.
   *
   * ```ts
   * const value: Guard<{ a: string }> = struct().required({ a: typeOf('string') });
   * ```
   */
  required<TGuardFns extends StructGuardFns>(fns: TGuardFns): StructGuard<T & StructRequired<TGuardFns>>;
  /**
   * Add optional properties to a `struct()` definition.
   *
   * ```ts
   * const value: Guard<{ a?: string }> = struct().optional({ a: typeOf('string') });
   * ```
   */
  optional<TGuardFns extends StructGuardFns>(fns: TGuardFns): StructGuard<T & StructOptional<TGuardFns>>;
};
